import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Collapse,
  Alert,
  IconButton,
  Fade,
  Divider,
  Stack,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person,
  Email,
  Cake,
  School,
  Work,
  LocationOn,
  Save,
  Cancel,
  Edit,
  Lock,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const UpdateProfile = ({ profile, onUpdate, renderTrigger }) => {
  const [formData, setFormData] = useState({
    username: profile.username || "",
    email: profile.email || "",
    age: profile.age || "",
    degree: profile.degree || "",
    experience: profile.experience || "",
    location: profile.location || "",
  });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClose = () => {
    setShowModal(false);
    setError(null);
    setFormData({
      username: profile.username || "",
      email: profile.email || "",
      age: profile.age || "",
      degree: profile.degree || "",
      experience: profile.experience || "",
      location: profile.location || "",
    });
  };

  const handleShow = () => setShowModal(true);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Prevent mouse wheel from changing number inputs.
  const handleWheel = (e) => {
    e.preventDefault();
    e.target.blur();
  };

  async function saveChanges(e) {
    e.preventDefault();

    if (formData.age && parseInt(formData.age) < 18) {
      setError("You must be at least 18 years old.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload = { ...formData, age: parseInt(formData.age) };

      const { data, status } = await axios.patch(
        `https://le-travaille-server.onrender.com/user/${profile.user_id}`,
        payload,
        { headers: { "Content-Type": "application/json" } },
      );

      if (status === 200) {
        toast.success("Profile updated successfully! 🎉");
        setIsLoading(false);
        handleClose();
        if (onUpdate) onUpdate(data);
        navigate("/user/dashboard");
      }
    } catch (err) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
    }
  }

  // Fields that should have wheel event disabled (number inputs).
  const numberFields = ["age", "experience"];

  const fieldConfigs = [
    {
      name: "username",
      label: "Username",
      icon: <Person />,
      disabled: true,
      helperText: "Username cannot be changed",
    },
    {
      name: "email",
      label: "Email Address",
      icon: <Email />,
      disabled: true,
      helperText: "Email cannot be changed",
    },
    {
      name: "age",
      label: "Age",
      icon: <Cake />,
      type: "number",
      required: true,
      inputProps: { min: 18, max: 120 },
    },
    {
      name: "degree",
      label: "Degree",
      icon: <School />,
      required: true,
      placeholder: "e.g., Bachelor's in Computer Science",
    },
    {
      name: "experience",
      label: "Experience",
      icon: <Work />,
      type: "number",
      required: true,
      placeholder: "e.g., 5",
    },
    {
      name: "location",
      label: "Location",
      icon: <LocationOn />,
      required: true,
      placeholder: "e.g., New York, USA",
    },
  ];

  return (
    <>
      {renderTrigger ? (
        renderTrigger(handleShow)
      ) : (
        <Button
          variant="outlined"
          onClick={handleShow}
          startIcon={<Edit />}
          fullWidth
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            py: 1.2,
            borderWidth: 2,
            borderColor: "#00786F",
            color: "#00786F",
            "&:hover": {
              borderWidth: 2,
              borderColor: "#005b54",
              backgroundColor: "rgba(0, 120, 111, 0.04)",
              transform: "scale(1.02)",
            },
            transition: "transform 0.2s ease-in-out",
          }}
        >
          Update Profile
        </Button>
      )}

      <Dialog
        open={showModal}
        onClose={handleClose}
        fullScreen={fullScreen}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 400 }}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 4 },
            overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
            maxHeight: "90vh",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            backgroundColor: "#00786F",
            color: "white",
            borderBottom: `1px solid rgba(255,255,255,0.1)`,
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Edit Profile
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                Update your personal information
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.15)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <form onSubmit={saveChanges}>
            <Stack spacing={2.5}>
              {fieldConfigs.map((field) => {
                const inputProps = {
                  ...field.inputProps,
                  ...(numberFields.includes(field.name) && {
                    onWheel: handleWheel,
                  }),
                };

                return (
                  <TextField
                    key={field.name}
                    fullWidth
                    name={field.name}
                    label={field.label}
                    type={field.type || "text"}
                    value={formData[field.name] || ""}
                    onChange={handleChange}
                    disabled={field.disabled}
                    required={field.required}
                    placeholder={field.placeholder}
                    helperText={field.helperText}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            sx={{
                              color: field.disabled
                                ? "action.disabled"
                                : "#00786F",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {field.icon}
                          </Box>
                        </InputAdornment>
                      ),
                      ...(field.disabled && {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Lock fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }),
                      inputProps: inputProps,
                    }}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        ...(field.disabled && {
                          bgcolor: "#f5f5f5",
                        }),
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: field.disabled ? "#d3d3d3" : "#00786F",
                        },
                      },
                      ...(numberFields.includes(field.name) && {
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                          {
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                        "& input[type=number]": {
                          MozAppearance: "textfield",
                        },
                      }),
                      "& .MuiInputLabel-root": {
                        color: field.disabled ? "#9e9e9e" : "#1A2332",
                        fontWeight: 500,
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: field.disabled ? "#9e9e9e" : "#00786F",
                      },
                      "& .MuiInputLabel-root.MuiFormLabel-filled": {
                        color: field.disabled ? "#9e9e9e" : "#1A2332",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: field.disabled ? "#d3d3d3" : "#e0e0e0",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: field.disabled ? "#d3d3d3" : "#00786F",
                      },
                    }}
                  />
                );
              })}

              <Collapse in={Boolean(error)}>
                <Alert
                  severity="error"
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => setError(null)}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  sx={{ borderRadius: 2 }}
                >
                  {error}
                </Alert>
              </Collapse>

              <Divider sx={{ my: 1 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  pt: 1,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  startIcon={<Cancel />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    borderWidth: 2,
                    borderColor: "#ccc",
                    color: "#1A2332",
                    "&:hover": {
                      borderWidth: 2,
                      borderColor: "#999",
                      backgroundColor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isLoading}
                  startIcon={!isLoading && <Save />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    minWidth: 120,
                    backgroundColor: "#00786F",
                    "&:hover": {
                      backgroundColor: "#005b54",
                      transform: "scale(1.05)",
                    },
                    transition: "transform 0.2s ease-in-out",
                    "&:disabled": {
                      backgroundColor: "rgba(0, 120, 111, 0.5)",
                      transform: "none",
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </Box>
            </Stack>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UpdateProfile;
